import React from "react";
import { Box, Textarea, Tooltip } from "@mui/joy";
import { SClose, SDelete, SEdit, SSave, SSend } from "../../../../../styles/StyledMUI";
import DarkTypography from "../../../../common/DarkTypography";
import IconButtonCTA from "../../../../common/IconButtonCTA";
import { colorTokens, textTokens, withAlpha } from "../../../../../styles/globalStyles";
import type { Comment } from "../../../../../api/zekay/comments";

type CriterionCommentsProps = {
  comments: Comment[];
  readOnly: boolean;
  currentUserName?: string;
  onAdd: (text: string) => void;
  onUpdate: (commentIndex: number, text: string) => void;
  onDelete: (commentIndex: number) => void;
};

const commentTextareaSx = {
  flex: 1,
  minWidth: 0,
  fontSize: "0.875rem",
  backgroundColor: colorTokens.bg.sheetLight,
  color: textTokens.color.bodyOnLight,
  borderColor: withAlpha(textTokens.color.bodyOnLight, 0.25),
  "& textarea": { color: textTokens.color.bodyOnLight },
  "& textarea::placeholder": { color: textTokens.color.onLightMuted, opacity: 1 },
  "&.Mui-focused": { borderColor: colorTokens.accent.primary },
  "--Textarea-focusedHighlight": colorTokens.accent.primary,
} as const;

function isSubmitShortcut(event: React.KeyboardEvent) {
  return event.key === "Enter" && (event.metaKey || event.ctrlKey);
}

type CommentActionProps = {
  title: string;
  ctaVariant: "accent" | "success" | "danger";
  onClick: () => void;
  disabled?: boolean;
  icon: React.ReactNode;
};

function CommentAction({ title, ctaVariant, onClick, disabled, icon }: CommentActionProps) {
  return (
    <Tooltip title={title}>
      <IconButtonCTA
        size="sm"
        variant="plain"
        ctaVariant={ctaVariant}
        aria-label={title}
        onClick={onClick}
        disabled={disabled}
      >
        {icon}
      </IconButtonCTA>
    </Tooltip>
  );
}

export default function CriterionComments({
  comments,
  readOnly,
  currentUserName,
  onAdd,
  onUpdate,
  onDelete,
}: CriterionCommentsProps) {
  const [draft, setDraft] = React.useState("");
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null);
  const [editValue, setEditValue] = React.useState("");

  const submitDraft = () => {
    const text = draft.trim();
    if (!text) return;
    onAdd(text);
    setDraft("");
  };

  const startEdit = (index: number) => {
    setEditingIndex(index);
    setEditValue(comments[index]?.comment ?? "");
  };

  const cancelEdit = () => setEditingIndex(null);

  const submitEdit = () => {
    const text = editValue.trim();
    if (editingIndex == null || !text) return;
    onUpdate(editingIndex, text);
    setEditingIndex(null);
  };

  if (readOnly && comments.length === 0) return null;

  return (
    <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
      {comments.map((comment, index) => {
        const isOwn = Boolean(currentUserName) && comment.user === currentUserName;
        const isEditingThis = !readOnly && editingIndex === index;

        return (
          <Box
            key={`${comment.timestamp}-${index}`}
            sx={{
              px: 1.25,
              py: 0.75,
              borderLeft: `3px solid ${colorTokens.accent.primary}`,
              backgroundColor: withAlpha(colorTokens.accent.primary, 0.1),
              borderRadius: "0 6px 6px 0",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  flexWrap: "wrap",
                  columnGap: 1,
                  minWidth: 0,
                  flex: 1,
                }}
              >
                <DarkTypography level="body-sm" colorVariant="onLight" sx={{ fontWeight: 600 }}>
                  {comment.user}
                </DarkTypography>
                <DarkTypography level="body-xs" colorVariant="onLightMuted">
                  {new Date(comment.timestamp).toLocaleString("fr-FR")}
                </DarkTypography>
              </Box>
              {!readOnly && isOwn && !isEditingThis && (
                <Box sx={{ display: "flex", flexShrink: 0 }}>
                  <CommentAction
                    title="Modifier le commentaire"
                    ctaVariant="accent"
                    onClick={() => startEdit(index)}
                    icon={<SEdit />}
                  />
                  <CommentAction
                    title="Supprimer le commentaire"
                    ctaVariant="danger"
                    onClick={() => onDelete(index)}
                    icon={<SDelete />}
                  />
                </Box>
              )}
            </Box>

            {isEditingThis ? (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 0.5 }}>
                <Textarea
                  autoFocus
                  minRows={2}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (isSubmitShortcut(e)) submitEdit();
                    if (e.key === "Escape") {
                      e.stopPropagation();
                      cancelEdit();
                    }
                  }}
                  aria-label="Modifier le commentaire"
                  sx={commentTextareaSx}
                />
                <CommentAction
                  title="Enregistrer le commentaire"
                  ctaVariant="success"
                  onClick={submitEdit}
                  disabled={!editValue.trim()}
                  icon={<SSave />}
                />
                <CommentAction
                  title="Annuler"
                  ctaVariant="danger"
                  onClick={cancelEdit}
                  icon={<SClose />}
                />
              </Box>
            ) : (
              <DarkTypography
                level="body-sm"
                colorVariant="onLight"
                sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", mt: 0.25 }}
              >
                {comment.comment}
              </DarkTypography>
            )}
          </Box>
        );
      })}

      {!readOnly && (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
          <Textarea
            minRows={1}
            value={draft}
            placeholder="Ajouter un commentaire"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (isSubmitShortcut(e)) submitDraft();
              if (e.key === "Escape" && draft) {
                e.stopPropagation();
                setDraft("");
              }
            }}
            aria-label="Ajouter un commentaire"
            sx={commentTextareaSx}
          />
          <CommentAction
            title="Ajouter le commentaire"
            ctaVariant="success"
            onClick={submitDraft}
            disabled={!draft.trim()}
            icon={<SSend />}
          />
        </Box>
      )}
    </Box>
  );
}
